<template>
  <PortalWidget
    class="cf-widget_holder"
    :widget="{ title: 'Call Flows', iconClass: 'fa fa-sitemap' }"
  >
    <template v-slot:headerContent>
      <div class="header-extra">
        <div class="btn-group">
          <button
            type="button"
            class="btn btn-secondary m-r-10"
            :disabled="isSavingDraft"
            @click="onSaveDraft()"
          >
            <Loader 
              v-if="isSavingDraft"
              custom-class="size-sm"
            />
            <template v-else>
              Save Draft
            </template>
          </button>
          <button
            type="button"
            class="btn btn-primary m-r-10"
            :disabled="isPublishing"
            @click="onPublish()"
          >
            <Loader 
              v-if="isPublishing"
              custom-class="size-sm"
            />
            <template v-else>
              Publish
            </template>
          </button>
        </div>
        <div class="cf-scene__name m-r-15">
          <InputText v-model="callFlowName" />
          <i
            class="fa fa-pencil"
            aria-hidden="true"
          />
        </div>
        <div
          v-if="hasCallFlowErrors"
          class="cf-error m-r-10"
        >
          <i
            class="fa fa-warning m-r-4"
            aria-hidden="true"
          />
          <span
            v-for="(error, index) in callFlowErrors"
            :key="index"
          >{{
            error.errorMessage
          }}</span>
        </div>
      </div>
    </template>

    <CallFlowView
      :scene="scene"
    />

    <CallFlowReferencesModal
      v-if="showModal"
      :call-flow-tree-references="callFlowTreeReferences"
      :save-draft="savingDraft"
      @closeModal="closeModal"
      @cloneAndSave="cloneAndSaveDraftCallFlow"
      @save="publishCallFlow"
    />
  </PortalWidget>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Loader from '@/core/components/Loader';
import CallFlowView from './CallFlowView';
import CallFlowReferencesModal from './CallFlowReferencesModal';
import InputText from './NodeProperties/Form/InputText';
import { STATUS_ERROR, STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';
import {
  getTreeSaveObject,
} from '../../helpers/save';
import NodeStyleData from '../../helpers/nodeStyleData';
import { utilities } from '../../../../angularBridgeCode/ringbaPortal.servicesModule';
import { $ngStateService, $ngTransitionService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import messages from '../../locales';

export default {
  name: 'CallFlowEditor',

  components: {
    PortalWidget,
    CallFlowView,
    CallFlowReferencesModal,
    InputText,
    Loader,
  },

  data() {
    return {
      callFlowTree: null,
      treeId: '',
      templateId: '',
      savingDraft: false,
      nodeStyleData: NodeStyleData,
      showModal: false,
      hasUpdated: false,
      killTransitionHandler: null,
    };
  },
  computed: {
    ...mapState({
      featureFlags: 'currentAccountFeatureFlags'
    }),
    ...mapState('callFlows', [
      'availableTemplates',
      'availableVoices',
      'callFlowTreeReferences',
    ]),

    ...mapGetters('callFlows', [
      'isDirty'
    ]),

    ...mapGetters('callFlows', [
      'getCallFlowTreeStatus',
      'getSaveTreeStatus',
      'getSaveDraftStatus',
      'nodesValidationMapping',
      'scene',
      'callFlowErrors'
    ]),
    callFlowName: {
      get() {
        return this.scene.name;
      },
      set(name) {
        this.setCallFlowName(name);
      }
    },

    ivrVoicesFeatureFlag() {
      return this.featureFlags.ivr_voices;
    },
    
    isCallFlowLoading() {
      return this.getCallFlowTreeStatus.status == STATUS_PENDING;
    },
    
    isSavingDraft() {
      return this.getSaveDraftStatus.status == STATUS_PENDING;
    },

    isPublishing() {
      return this.getSaveTreeStatus.status == STATUS_PENDING;
    },

    hasCallFlowErrors() {
      return this.callFlowErrors && this.callFlowErrors.length;
    },
  },
  async created() {
    await this.getAvailableNodes();
    await this.getAvailableVoices();

    this.treeId = this.$attrs.treeid;
    this.templateId = this.$attrs.templateid;
    this.addLeavePageListener();
    try {
      if (this.treeId) {
        this.callFlowTree = await this.getCallFlowTree(this.treeId);
      } else if (this.templateId) {
        await this.getAvailableTemplates();
        const callFlowTemplate = this.availableTemplates.find(
          (template) => template.id === this.templateId
        );
        const payload = {
          templateId: callFlowTemplate.id,
          name: 'New ' + callFlowTemplate.name,
        };
        this.callFlowTree = await this.initializeCallFlowTreeTemplate(payload);
      } else {
        this.callFlowTree = await this.initializeCallFlowTree();
      }

      if (this.callFlowTree) {
        // Validate the retrieved tree
        // await this.saveTree(this.callFlowTree);

        await this.getCallFlowTreeReferences(this.callFlowTree.id);
      }
    } catch (e) {
      console.error(e);
      // error handled in store if getCallFlowTree fails

      // Once callFlowTree is set above, hit the finalizeTree endpoint to validate nodes, if error, set the node errors below and prepare the scene
      if (this.callFlowTree) {
        if (e.nodeErrors?.length) {
          e.nodeErrors.forEach((error) => {
            this.callFlowTree.nodes[error.nodeId].node.nodeErrors = error;
          });
        }
      }
    }
  },
  beforeDestroy() {
    this.killTransitionHandler();
    window.removeEventListener('beforeunload', this.beforeUnloadListener);
  },
  methods: {
    ...mapActions('callFlows', [
      'getAvailableNodes',
      'getAvailableTemplates',
      'getAvailableVoices',
      'getCallFlowTreeReferences',
      'getCallFlowTree',
      'initializeCallFlowTree',
      'initializeCallFlowTreeTemplate',
      'saveTree',
      'saveDraft',
      'cloneAndSaveDraft',
      'setCallFlowName'
    ]),
    async onSaveDraft() {
      if (this.callFlowTreeReferences.hasReferences) {
        // If
        this.savingDraft = true;
        this.showModal = true;
      } else {
        await this.saveDraftCallFlow(this.callFlowTree.id);
      }
    },

    async onPublish() {
      if (this.callFlowTreeReferences.hasReferences) {
        // If
        this.savingDraft = false;
        this.showModal = true;
      } else {
        await this.publishCallFlow();
      }
    },

    addLeavePageListener() {
      this.killTransitionHandler = $ngTransitionService.onBefore({}, this.confirmOnStateChange);
      window.addEventListener('beforeunload', this.beforeUnloadListener);
    },

    beforeUnloadListener(e) {
      if (this.isDirty) {
        const confirmationMessage = this.$t('CALL_FLOW_UNLOAD_CONFIRMATION');

        (e || window.event).returnValue = confirmationMessage; //Gecko + IE
        return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
      }
    },

    confirmOnStateChange() {
      if (!this.isDirty) {
        return;
      }

      return confirm(this.$t('CALL_FLOW_UNLOAD_CONFIRMATION'));
    },
    
    async cloneAndSaveDraftCallFlow() {
      let newTree;
      try {
        newTree = await this.cloneAndSaveDraft(
          getTreeSaveObject(this.callFlowTree.id, this.scene)
        );
      } finally {
        this.handleSavingResults(newTree?.ivrTree.id, this.getSaveDraftStatus.status, 'SAVE_DRAFT_SUCESS', 'SAVE_DRAFT_ERROR');
        this.showModal = false;
      }
    },

    async saveDraftCallFlow(callFlowId) {
      try {
        await this.saveDraft(
          getTreeSaveObject(callFlowId, this.scene)
        );
      } finally {
        this.handleSavingResults(callFlowId, this.getSaveDraftStatus.status, 'SAVE_DRAFT_SUCESS', 'SAVE_DRAFT_ERROR');
      }
    },
    async publishCallFlow() {
      try {
        await this.saveTree(
          getTreeSaveObject(this.callFlowTree.id, this.scene)
        );
      } finally {
        this.handleSavingResults(this.callFlowTree.id, this.getSaveTreeStatus.status, 'SAVE_CALL_FLOW_SUCESS', 'SAVE_CALL_FLOW_ERROR');
        this.showModal = false;
      }
    },

    handleSavingResults(callFlowId, saveStatus, saveSuccessMessage, saveErrorMessage) {
      if (saveStatus === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t(saveSuccessMessage));

        if (!this.treeId || this.treeId !== callFlowId) {
          $ngStateService.go('dashboard.call-flow.edit', {
            treeId: callFlowId,
          });
        }
      }

      if (saveStatus === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.$t(saveErrorMessage));
      }
    },
    
    closeModal() {
      this.showModal = false;
    }
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>